/**
 * Automate hooks (v1.1-C2) — full real-data binding for the
 * /automate UI. Replaces MOCK_WORKFLOWS / MOCK_AUTOMATIONS /
 * MOCK_PLAYBOOKS / MOCK_EXECUTIONS reads.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  Workflow,
  Playbook,
  ExecutionEntry,
  Automation,
  TriggerType,
  EventType,
} from './mock-automate-data';

// ─── server VM (mirrors automate-workflows.ts shapes) ──────────────

export interface ServerWorkflow {
  id: string;
  name: string;
  description: string;
  status: string;
  isEnabled: boolean;
  nodes: Array<{
    id: string;
    type: string;
    position: { x: number; y: number };
    data: { label: string; config: Record<string, unknown> };
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle?: string | null;
    label?: string | null;
    animated: boolean;
  }>;
  nodeCount: number;
  runCount: number;
  lastRunAt: string | null;
  avgDurationMs: number;
  successRate: number;
  purpose: string;
  successCriteria: Array<{
    id: string;
    description: string;
    type: string;
    evaluation: string;
    weight: string;
  }>;
  constraints: string[];
  supervisorMode: string;
  triggerType: string;
  cronExpression: string | null;
  eventType: string | null;
  complexity: string;
  agentId: string | null;
  agentName: string | null;
  agentIcon: string | null;
  agentIconColor: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface ServerExecution {
  id: string;
  type: string;
  sourceId: string;
  sourceName: string;
  agentId: string | null;
  agentName: string | null;
  status: string;
  trigger: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number;
  stepsCompleted: number;
  totalSteps: number;
  // Bug #722 — real failed step count from workflow_step_executions,
  // not derived from terminal `status`. Optional for back-compat with
  // older mock fixtures that don't set it.
  stepsFailed?: number;
}

export interface ServerPlaybook {
  id: string;
  name: string;
  description: string;
  category: string;
  agentId: string | null;
  agentName: string | null;
  agentIcon: string | null;
  agentIconColor: string | null;
  steps: unknown[];
  estimatedDuration: string | null;
  successRate: number;
  tags: string[];
}

// ─── adapters ──────────────────────────────────────────────────────

// Server uses past-tense `scheduled`; mock UI uses `schedule`.
const TRIGGER_TYPE_MAP: Record<string, Workflow['triggerType']> = {
  scheduled: 'schedule',
  schedule: 'schedule',
  event: 'event',
  webhook: 'webhook',
  manual: 'manual',
};

function adaptWorkflow(s: ServerWorkflow): Workflow {
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    status: (s.status as Workflow['status']) ?? 'draft',
    isEnabled: s.isEnabled ?? s.status === 'active',
    nodes: s.nodes.map((n) => ({
      id: n.id,
      type: n.type as Workflow['nodes'][number]['type'],
      position: n.position,
      data: { label: n.data.label, config: n.data.config },
    })),
    edges: s.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? undefined,
      label: e.label ?? undefined,
      animated: e.animated,
    })),
    nodeCount: s.nodeCount,
    runCount: s.runCount,
    lastRunAt: s.lastRunAt,
    avgDurationMs: s.avgDurationMs,
    successRate: s.successRate,
    purpose: s.purpose,
    successCriteria: s.successCriteria.map((c) => ({
      id: c.id,
      description: c.description,
      type: c.type as Workflow['successCriteria'][number]['type'],
      evaluation:
        c.evaluation as Workflow['successCriteria'][number]['evaluation'],
      weight: c.weight as Workflow['successCriteria'][number]['weight'],
    })),
    constraints: s.constraints,
    supervisorMode: s.supervisorMode as Workflow['supervisorMode'],
    triggerType: TRIGGER_TYPE_MAP[s.triggerType] ?? 'manual',
    cronExpression: s.cronExpression ?? undefined,
    eventType: (s.eventType as EventType | null) ?? undefined,
    complexity: s.complexity as Workflow['complexity'],
    agentId: s.agentId ?? undefined,
    agentName: s.agentName ?? undefined,
    agentIcon: s.agentIcon ?? undefined,
    agentIconColor: s.agentIconColor ?? undefined,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
    createdBy: s.createdBy,
  };
}

function adaptExecution(s: ServerExecution): ExecutionEntry {
  const status = s.status.toLowerCase();
  return {
    id: s.id,
    type: s.type as ExecutionEntry['type'],
    sourceId: s.sourceId,
    sourceName: s.sourceName,
    agentId: s.agentId ?? undefined,
    agentName: s.agentName ?? undefined,
    agentIcon: 'bot',
    agentIconColor: '#1890FF',
    // #669 — Phase-2 QA caught a stats mismatch: the History page reported
    // `Successful 7` while the executions API actually had `success=6` (one
    // extra row was getting mapped to 'completed' because the pre-fix
    // catch-all branch silently rewrote any unknown server status — most
    // visibly `suspended` — to 'completed'. Mapping a paused-for-approval
    // run as "successful" violates "truth over speed".
    //
    // Server vocabulary today: success | succeeded | completed |
    //   failure | failed | pending | running | suspended | cancelled.
    // UI vocabulary (ExecutionStatus): completed | failed | running |
    //   cancelled.
    //
    // Honest mapping:
    //   success / succeeded / completed → 'completed'
    //   failure / failed                → 'failed'
    //   pending / running / queued      → 'running'
    //   suspended           → 'running'  (still in flight from the
    //                                     operator's perspective — awaiting
    //                                     approval, NOT a successful run)
    //   cancelled           → 'cancelled'
    //   anything else       → 'running'  (preserve in-flight semantics so
    //                                     unknown states never inflate the
    //                                     success/failure counts)
    status: (
      status === 'success' || status === 'succeeded' || status === 'completed'
        ? 'completed'
        : status === 'failure' || status === 'failed'
        ? 'failed'
        : status === 'cancelled'
        ? 'cancelled'
        : 'running'
    ) as ExecutionEntry['status'],
    trigger: s.trigger,
    startedAt: s.startedAt,
    completedAt: s.completedAt,
    durationMs: s.durationMs,
    stepsCompleted: s.stepsCompleted,
    totalSteps: s.totalSteps,
    // Bug #722 — wire real stepsFailed through. Default 0 keeps existing
    // history rows valid while older mocks still flow through.
    stepsFailed: s.stepsFailed ?? 0,
  };
}

function adaptPlaybook(s: ServerPlaybook): Playbook {
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    category: s.category as Playbook['category'],
    agentId: s.agentId ?? '',
    agentName: s.agentName ?? '',
    agentIcon: s.agentIcon ?? 'book',
    agentIconColor: s.agentIconColor ?? '#1890FF',
    steps: (s.steps as Playbook['steps']) ?? [],
    estimatedDuration: s.estimatedDuration ?? '',
    successRate: s.successRate,
    tags: s.tags,
  };
}

// Workflow → Automation (legacy shape for MOCK_AUTOMATIONS surfaces)
function workflowToAutomation(w: Workflow): Automation {
  return {
    id: w.id,
    name: w.name,
    description: w.description,
    agentId: w.agentId ?? '',
    agentName: w.agentName ?? '',
    agentIcon: w.agentIcon ?? 'bot',
    agentIconColor: w.agentIconColor ?? '#1890FF',
    triggerType: (w.triggerType === 'webhook' || w.triggerType === 'manual'
      ? 'event'
      : (w.triggerType as TriggerType)),
    cronExpression: w.cronExpression,
    eventType: w.eventType,
    isEnabled: w.isEnabled ?? w.status === 'active',
    lastRunAt: w.lastRunAt,
    nextRunAt: null,
    status: (w.status === 'active' ? 'active' : w.status === 'paused' ? 'paused' : 'error') as Automation['status'],
    successRate: w.successRate,
    totalRuns: w.runCount,
    createdAt: w.createdAt,
    createdBy: w.createdBy,
  };
}

// ─── hooks ─────────────────────────────────────────────────────────

export function useWorkflows() {
  return useQuery<{ workflows: ServerWorkflow[] }, Error, Workflow[]>({
    queryKey: ['automate-workflows'],
    queryFn: () =>
      apiClient.get<{ workflows: ServerWorkflow[] }>('/automate-workflows'),
    select: (r) => r.workflows.map(adaptWorkflow),
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery<ServerWorkflow, Error, Workflow | null>({
    queryKey: ['automate-workflows', id],
    enabled: !!id,
    queryFn: () =>
      apiClient.get<ServerWorkflow>(`/automate-workflows/${id}`),
    select: (r) => (r ? adaptWorkflow(r) : null),
  });
}

export function useAutomations() {
  return useQuery<{ workflows: ServerWorkflow[] }, Error, Automation[]>({
    queryKey: ['automate-workflows', 'as-automations'],
    queryFn: () =>
      apiClient.get<{ workflows: ServerWorkflow[] }>('/automate-workflows'),
    select: (r) => r.workflows.map(adaptWorkflow).map(workflowToAutomation),
  });
}

/**
 * Execution history list.
 *
 * #197 / #205 — the History page MUST surface a freshly-fired Test Run
 * row promptly. Before this fix the hook used the QueryClient default
 * (`staleTime: 5min`), so a user who clicked Test Run on the workflow
 * page then navigated to History inside 5 minutes saw the cached
 * pre-run list and concluded the new run was never recorded.
 *
 * Fix:
 *   1. `staleTime: 0` — the list is always considered stale; a tab
 *      switch back to History always re-fetches.
 *   2. `refetchOnMount: 'always'` — explicit re-fetch on remount even
 *      if the cache has data; the History page is the primary
 *      observability surface for runs.
 *   3. Built-in auto-poll while in-flight — the hook inspects its
 *      OWN cached result via the react-query function form of
 *      `refetchInterval`, so it polls every 2s while any row is
 *      `running` or `pending` and falls silent on idle tenants.
 *      No need for callers to compute this themselves; the
 *      single-hook design avoids the dual-observer footgun where
 *      one observer's poll interval drifts from another's.
 *
 * The workflow page also invalidates `['automate-executions']` on
 * terminal state of an active execution; together with the in-flight
 * auto-poll, the History page now animates `running → completed`
 * without manual refresh.
 */
export function useExecutions(opts: { pollMs?: number | false } = {}) {
  return useQuery<
    { executions: ServerExecution[] },
    Error,
    ExecutionEntry[]
  >({
    queryKey: ['automate-executions'],
    queryFn: () =>
      apiClient.get<{ executions: ServerExecution[] }>(
        '/automate-executions',
      ),
    select: (r) => r.executions.map(adaptExecution),
    // #197 / #205 — list is the primary post-run observability surface;
    // never serve cached data here.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      // Caller-provided override takes precedence (legacy callers
      // pass false to disable polling explicitly).
      if (opts.pollMs === false) return false;
      if (typeof opts.pollMs === 'number') return opts.pollMs;
      // Default behaviour: auto-poll every 2s while any row is in a
      // non-terminal status. Inspect the raw server response (not the
      // adapted `select` output) so we don't depend on adapter shape.
      const data = query.state.data as { executions: ServerExecution[] } | undefined;
      if (!data) return false;
      const inFlight = data.executions.some(
        (e) => e.status === 'running' || e.status === 'pending',
      );
      return inFlight ? 2000 : false;
    },
  });
}

/**
 * History detail VM — full execution trace including per-step status,
 * outputs, errors, and aggregate counts.
 *
 * NEW-MAJOR-2 (round 7) — backs the new
 * `/automate/history/:executionId` drill-down route. Resolves the
 * execution by id alone, so the History list does not need to know
 * the workflow id to link to a row.
 */
export interface HistoryExecutionStep {
  id: string;
  workflowNodeId: string | null;
  nodeKind: string | null;
  label: string | null;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  output: unknown;
  error: string | null;
  displayOrder: number;
}

export interface HistoryExecutionDetail {
  id: string;
  automationId: string;
  automationName: string | null;
  status: string;
  triggeredBy: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  error: string | null;
  stepsCompleted: number;
  totalSteps: number;
  steps: HistoryExecutionStep[];
}

export function useHistoryExecution(
  executionId: string | undefined,
  opts: { refetchIntervalMs?: number } = {},
) {
  return useQuery<HistoryExecutionDetail, Error>({
    queryKey: ['automate-executions', executionId],
    enabled: !!executionId,
    queryFn: () =>
      apiClient.get<HistoryExecutionDetail>(
        `/automate-executions/${executionId}`,
      ),
    refetchInterval: (query) => {
      const data = query.state.data as HistoryExecutionDetail | undefined;
      // Stop polling once execution reaches a terminal state so an
      // in-flight detail page that lands on a still-running row
      // animates to its terminal state without manual refresh.
      if (!data) return opts.refetchIntervalMs ?? 1500;
      const status = data.status.toLowerCase();
      const terminal = [
        'success',
        'succeeded',
        'completed',
        'failed',
        'failure',
        'suspended',
        'cancelled',
      ];
      if (terminal.includes(status)) return false;
      return opts.refetchIntervalMs ?? 1500;
    },
  });
}

export function usePlaybooks() {
  return useQuery<{ playbooks: ServerPlaybook[] }, Error, Playbook[]>({
    queryKey: ['playbooks'],
    queryFn: () => apiClient.get<{ playbooks: ServerPlaybook[] }>('/playbooks'),
    select: (r) => r.playbooks.map(adaptPlaybook),
  });
}

// ─── mutations ─────────────────────────────────────────────────────

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  purpose?: string;
  triggerType: 'scheduled' | 'event' | 'webhook' | 'manual';
  cronExpression?: string | null;
  eventType?: string | null;
  eventConditions?: Record<string, unknown> | null;
  supervisorMode?: 'minimal' | 'standard' | 'thorough';
  complexity?: 'simple' | 'standard' | 'complex';
  agentId?: string | null;
  nodes?: Array<{
    /**
     * Optional client-supplied UUID. When set, the backend honors it
     * so edges in the same payload can reference it. Used by the
     * Simple Agent template (#237).
     */
    id?: string;
    nodeKind: string;
    label?: string;
    positionX: number;
    positionY: number;
    config?: Record<string, unknown>;
  }>;
  edges?: Array<{
    sourceNodeId: string;
    targetNodeId: string;
    sourceHandle?: string;
    label?: string;
    animated?: boolean;
  }>;
  successCriteria?: Array<{
    description: string;
    criteriaType?: 'completeness' | 'accuracy' | 'format' | 'safety' | 'compliance';
    evaluation?: 'ai' | 'deterministic' | 'hybrid';
    weight?: 'critical' | 'important' | 'nice_to_have';
  }>;
  constraints?: string[];
}

export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreateWorkflowInput>({
    mutationFn: (input) =>
      apiClient.post<{ id: string }>('/automate-workflows', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automate-workflows'] });
    },
  });
}

// ─── execution hooks (the "real Test Run" path) ───────────────────

export interface ExecutionStepRow {
  id: string;
  workflowNodeId: string | null;
  nodeKind: string | null;
  label: string | null;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  output: unknown;
  error: string | null;
  displayOrder: number;
}

export interface SupervisorPreview {
  derivedRole: string;
  watchpoints: string[];
  redLines: string[];
  mode: 'minimal' | 'standard' | 'thorough';
}

export interface ExecutionDetail {
  id: string;
  automationId: string;
  status: string;
  triggeredBy: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  error: string | null;
  steps: ExecutionStepRow[];
  supervisor: SupervisorPreview | null;
}

export interface UpdateWorkflowLifecycleInput {
  workflowId: string;
  status?: 'draft' | 'active' | 'paused' | 'archived';
  isEnabled?: boolean;
}

export function useUpdateWorkflowLifecycle() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; status: string; isEnabled: boolean },
    Error,
    UpdateWorkflowLifecycleInput
  >({
    mutationFn: ({ workflowId, ...body }) =>
      apiClient.patch<{ id: string; status: string; isEnabled: boolean }>(
        `/automate-workflows/${workflowId}`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['automate-workflows'] });
      void qc.invalidateQueries({
        queryKey: ['automate-workflows', vars.workflowId],
      });
      void qc.invalidateQueries({ queryKey: ['automate-workflows', 'as-automations'] });
    },
  });
}

/**
 * POST /automate-workflows/:id/execute — fires a real test run that
 * walks the saved workflow definition, calls real LLMs / tools where
 * wired, and writes per-step rows the canvas polls back via
 * `useExecutionDetail`.
 *
 * #200 — invalidates `['automate-executions']` and
 * `['automate-workflows']` immediately on success so the History
 * page (and the workflow list's run_count / success_rate) refresh
 * the moment the user clicks Test Run instead of showing stale
 * cache. The runner finishes async; consumers re-poll via
 * `useExecutionDetail` for live step state.
 */
export function useExecuteWorkflow() {
  const qc = useQueryClient();
  return useMutation<
    { executionId: string; status: string },
    Error,
    { workflowId: string; triggerData?: Record<string, unknown> }
  >({
    mutationFn: ({ workflowId, triggerData }) =>
      apiClient.post<{ executionId: string; status: string }>(
        `/automate-workflows/${workflowId}/execute`,
        triggerData ? { triggerData } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automate-executions'] });
      void qc.invalidateQueries({ queryKey: ['automate-workflows'] });
    },
  });
}

/**
 * Polled view of one execution. Returns immediately while running and
 * keeps re-fetching every `refetchInterval`ms until the status is
 * terminal. The canvas uses this to animate node statuses with REAL
 * data, not random simulated transitions.
 */
export function useExecutionDetail(
  workflowId: string | undefined,
  executionId: string | null | undefined,
  opts: { refetchIntervalMs?: number } = {},
) {
  return useQuery<ExecutionDetail, Error>({
    queryKey: ['automate-workflows', workflowId, 'executions', executionId],
    enabled: !!workflowId && !!executionId,
    queryFn: () =>
      apiClient.get<ExecutionDetail>(
        `/automate-workflows/${workflowId}/executions/${executionId}`,
      ),
    refetchInterval: (query) => {
      const data = query.state.data as ExecutionDetail | undefined;
      // Stop polling once execution reaches a terminal state.
      if (!data) return opts.refetchIntervalMs ?? 1000;
      const status = data.status.toLowerCase();
      if (
        [
          'success',
          'succeeded',
          'completed',
          'failure',
          'failed',
          'suspended',
          'cancelled',
        ].includes(status)
      ) {
        return false;
      }
      return opts.refetchIntervalMs ?? 1000;
    },
  });
}

/**
 * GET /automate-workflows/:id/supervisor-context — preview of what
 * the auto-derived AI supervisor will know about this workflow.
 */
export function useSupervisorContext(workflowId: string | undefined) {
  return useQuery<SupervisorPreview, Error>({
    queryKey: ['automate-workflows', workflowId, 'supervisor-context'],
    enabled: !!workflowId,
    queryFn: () =>
      apiClient.get<SupervisorPreview>(
        `/automate-workflows/${workflowId}/supervisor-context`,
      ),
  });
}

export function useInstantiatePlaybook() {
  const qc = useQueryClient();
  return useMutation<
    { id: string },
    Error,
    { playbookId: string; name?: string }
  >({
    mutationFn: ({ playbookId, name }) =>
      apiClient.post<{ id: string }>(
        `/playbooks/${playbookId}/instantiate`,
        name ? { name } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automate-workflows'] });
    },
  });
}

export function usePlaybook(id: string | undefined) {
  return useQuery<ServerPlaybook, Error, Playbook | null>({
    queryKey: ['playbooks', id],
    enabled: !!id,
    queryFn: () => apiClient.get<ServerPlaybook>(`/playbooks/${id}`),
    select: (r) => (r ? adaptPlaybook(r) : null),
  });
}
